<template>
  <v-dialog
    v-model="dialogState"
    max-width="600"
    min-width="300"
    transition="dialog-bottom-transition"
    @keydown.esc="onEscPushed"
    @click:outside="onOutsideClicked"
  >
    <div>
      <v-toolbar dense color="secondary">
        <v-spacer></v-spacer>
        <v-toolbar-title style="color:white;">死活管理</v-toolbar-title>
        <v-spacer></v-spacer>
      </v-toolbar>

      <v-container v-if="selectedDevice" style="background: white;">
        <v-row>
          <v-col cols="4">デバイス名</v-col>
          <v-col cols="8">{{ selectedDevice.name }}</v-col>
        </v-row>

        <v-divider></v-divider>

        <v-row>
          <v-col cols="4" class="item-label">
            <div>メール通知</div>
          </v-col>
          <v-col cols="8">
            <v-switch
              v-model="notifSettings.active"
              @change="onActiveChanged"
            />
          </v-col>
        </v-row>
        <v-row>
          <p class="text--secondary">
            ※&nbsp;オフライン・オフラインの変更時にメールで通知をします
          </p>
        </v-row>
      </v-container>

      <v-data-table
        :headers="headers"
        :items="receiverInfos"
        :loading="receiversLoading"
        loading-text="ロード中"
        no-data-text="受信アドレスはありません"
      >
        <!-- Top of Receiver Table -->
        <template v-slot:top>
          <v-container>
            <v-row>
              <v-col cols="5">
                <v-edit-dialog
                  large
                  :return-value.sync="newReceiverEmail"
                  @save="onAddReceiverClicked"
                  @cancel="clearNewEmailInput"
                >
                  <v-btn color="user">Eメールアドレス追加</v-btn>
                  <template v-slot:input>
                    <validate-observer ref="addEmailObserver">
                      <validate-provider
                        v-slot="{ errors }"
                        name="addEmail"
                        rules="required|email"
                      >
                        <v-text-field
                          v-model="newReceiverEmail"
                          :error-messages="errors"
                          single-line
                          autofocus
                        />
                      </validate-provider>
                    </validate-observer>
                  </template>
                </v-edit-dialog>
              </v-col>
              <v-col cols="7"></v-col>
            </v-row>
          </v-container>
        </template>

        <!-- Receiver Table List -->
        <template v-slot:[`item.isReceive`]="{ item }">
          <v-switch
            v-model="item.isReceive"
            @change="onIsReceiveChanged(item)"
          />
        </template>

        <template v-slot:[`item.email`]="{ item }">
          <v-edit-dialog
            large
            :return-value.sync="item.email"
            @save="onUpdateReceiverClicked(item.id, item.email)"
          >
            <span>{{ item.email }}</span>
            <v-btn icon large>
              <v-icon>mdi-pencil</v-icon>
            </v-btn>

            <template v-slot:input>
              <validate-observer ref="updateEmailObserver">
                <validate-provider
                  v-slot="{ errors }"
                  name="updateEmail"
                  rules="required|email"
                >
                  <v-text-field
                    v-model="item.email"
                    single-line
                    autofocus
                    :error-messages="errors"
                  />
                </validate-provider>
              </validate-observer>
            </template>
          </v-edit-dialog>
        </template>

        <template v-slot:[`item.delete`]="{ item }">
          <v-btn icon @click="onDeleteReceiverClicked(item.id)">
            <v-icon large>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </div>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ValidateProvider, ValidateObserver } from '@/plugins/validators'
import {
  getUserNotifs,
  updateUserNotif,
  addUserReceiver,
  updateUserReceiver,
  deleteUserReceiver
} from '@/firebaseAccess/firestoreAccess'

const headerList = [
  {
    filterable: false,
    text: '受信',
    sortable: false,
    value: 'isReceive'
  },
  {
    filterable: false,
    text: 'Eメールアドレス',
    sortable: false,
    value: 'email'
  },
  {
    filterable: false,
    text: '削除',
    sortable: false,
    value: 'delete'
  }
]

export default {
  name: 'NotifSetDialog',
  props: {
    dialogOpen: {
      type: Boolean,
      required: true
    },
    selectedDevice: {
      type: Object,
      required: true
    },
    deviceNotifSettings: {
      type: Object,
      required: true
    }
  },
  components: {
    ValidateProvider,
    ValidateObserver
  },
  data: () => {
    return {
      switchMock: false,

      dialogState: false,
      notifSettings: {
        active: false,
        receiverIds: []
      },
      newReceiverEmail: '',
      headers: headerList,
      receiversLoading: false,
      selectedReceivers: []
    }
  },
  computed: {
    ...mapGetters({
      userId: 'users/getActiveUserId',
      receivers: 'users/receivers/getReceivers'
    }),
    receiverInfos() {
      const infos = []
      if (this.receivers.length > 0) {
        for (const receiver of this.receivers) {
          const receiverInfo = {
            id: receiver.id,
            email: receiver.email,
            // Need to add isReceive prop to receivers for display.
            isReceive: this.notifSettings.receiverIds.includes(receiver.id)
          }
          infos.push(receiverInfo)
        }
      }

      return infos
    }
  },
  methods: {
    ...mapActions({
      showSnack: 'snackbar/showSnack',
      bindReceiversRef: 'users/receivers/bindRef'
    }),
    onEscPushed() {
      this.$emit('escPushed')
    },
    onOutsideClicked() {
      this.$emit('outsideClicked')
    },
    async onActiveChanged() {
      await updateUserNotif(this.userId, this.selectedDevice.id, {
        active: this.notifSettings.active
      })
    },
    async onAddReceiverClicked() {
      const validated = await this.$refs.addEmailObserver.validate()
      if (validated) {
        await addUserReceiver(this.userId, { email: this.newReceiverEmail })

        this.clearNewEmailInput()

        this.showSnack({ message: 'Eメールアドレスを追加しました' })

        return
      }

      this.showSnack({
        message: 'Eメールアドレスの入力が不正なため追加に失敗しました',
        color: 'error'
      })
    },
    clearNewEmailInput() {
      this.newReceiverEmail = ''
    },
    async onUpdateReceiverClicked(receiverId, email) {
      const receiver = this.receivers.find(r => r.id === receiverId)

      const validated = await this.$refs.updateEmailObserver.validate()
      if (validated) {
        if (receiver) {
          await updateUserReceiver(this.userId, receiver.id, { email })
        }

        this.showSnack({ message: 'Eメールアドレスを更新しました' })
      } else {
        this.showSnack({
          message: 'Eメールアドレスの入力が不正なため追加に失敗しました',
          color: 'error'
        })
      }
    },
    async onDeleteReceiverClicked(receiverId) {
      const notifsRef = await getUserNotifs(this.userId)

      // Get all notifications.
      const notifs = []
      for (const doc of notifsRef.docs) {
        const notif = doc.data()
        notif.id = doc.id
        notifs.push(notif)
      }

      const tasks = []
      // Collect update action if notif is using receiver who will be deleted.
      for (const notif of notifs) {
        if (notif.receiverIds.includes(receiverId)) {
          // Make new receiver id array which doesn't have receiver to delete.
          const newReceiverIds = notif.receiverIds.filter(i => i !== receiverId)
          const task = updateUserNotif(this.userId, notif.id, {
            receiverIds: newReceiverIds
          })

          tasks.push(task)

          if (notif.id === this.selectedDevice.id) {
            this.notifSettings.receiverIds = newReceiverIds
          }
        }
      }

      // Execute all update actions
      await Promise.all(tasks).then(() => {})

      await deleteUserReceiver(this.userId, receiverId)
    },
    async onIsReceiveChanged(receiver) {
      const currentReceiverIds = []

      if ('receiverIds' in this.notifSettings) {
        this.notifSettings.receiverIds.forEach(i => currentReceiverIds.push(i))
      }

      let newReceiverIds = null
      if (receiver.isReceive) {
        // If activated, add a receiver id to current id list.
        newReceiverIds = [...currentReceiverIds, receiver.id]
      } else {
        // If deactivated, remove a receiver id from current id list.
        newReceiverIds = currentReceiverIds.filter(i => i !== receiver.id)
      }

      this.notifSettings.receiverIds = newReceiverIds

      await updateUserNotif(this.userId, this.selectedDevice.id, {
        receiverIds: newReceiverIds
      })
    }
  },
  watch: {
    dialogOpen: function(newVal) {
      this.dialogState = newVal
    },
    deviceNotifSettings: function(newVal) {
      if ('active' in newVal) {
        this.notifSettings.active = newVal.active
      } else {
        // active doesn't exists yet in newVal, then initialize variables.
        this.notifSettings.active = false
      }

      if ('receiverIds' in newVal) {
        this.notifSettings.receiverIds = newVal.receiverIds
      } else {
        // receiverIds doesn't exists yet in newVal, then initialize variables.
        this.notifSettings.receiverIds = []
        this.receivers.forEach(r => (r.isReceive = false))
      }
    }
  },
  created() {
    this.bindReceiversRef().then(() => {})
  }
}
</script>

<style scoped>
.item-label {
  display: flex;
  align-items: center;
}
</style>
